import { Link } from "react-router";
import { formatRelativeTime } from "@/lib/format/relative-time";
import type { FeedbackDetail } from "@/lib/feedback/feedback-detail";
import { formatAdminDateTime } from "@/lib/format/admin-time";
import { formatPhone } from "@/lib/format/phone";

export interface FeedbackDetailViewProps {
  feedback: FeedbackDetail;
  now: Date;
}

const CATEGORY_META: Record<
  string,
  { emoji: string; tone: "bad" | "info" | "warn" | "neutral" }
> = {
  bug: { emoji: "🐞", tone: "bad" },
  feature_request: { emoji: "💡", tone: "info" },
  improvement: { emoji: "🔧", tone: "warn" },
  other: { emoji: "❓", tone: "neutral" },
};

const STATUS_TONE: Record<string, "good" | "warn" | "neutral"> = {
  new: "warn",
  reviewed: "neutral",
  resolved: "good",
};

function Chip({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "info" | "bad";
  children: React.ReactNode;
}) {
  const cls = {
    neutral: "text-fc-text-secondary border-fc-border bg-fc-overlay",
    good: "text-fc-brand border-fc-brand/40 bg-fc-brand/10",
    warn: "text-fc-warn border-fc-warn/40 bg-fc-warn/10",
    info: "text-fc-info border-fc-info/40 bg-fc-info/10",
    bad: "text-fc-bad border-fc-bad/40 bg-fc-bad/10",
  }[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
    >
      {children}
    </span>
  );
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map(p => p[0]?.toUpperCase() ?? "")
      .join("") || "??"
  );
}

// Moments: admins' clock (IST), labelled.
const toDateTime = formatAdminDateTime;

export function FeedbackDetailView({ feedback, now }: FeedbackDetailViewProps) {
  const meta = CATEGORY_META[feedback.category] ?? CATEGORY_META.other;
  const statusTone = STATUS_TONE[feedback.status] ?? "neutral";

  return (
    <div className="flex flex-col gap-6">
      {/* HERO */}
      <section className="flex items-center gap-4">
        <div className="grid h-[72px] w-[72px] place-items-center rounded-2xl bg-gradient-to-br from-fc-warn to-fc-bad text-[32px]">
          {meta.emoji}
        </div>
        <div className="min-w-0">
          <h1 className="text-[24px] font-semibold tracking-tight text-fc-text">
            {feedback.userName}
          </h1>
          <div className="font-mono text-[13px] text-fc-text-dim">
            {feedback.userEmail}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip tone={meta.tone}>{feedback.category}</Chip>
            <Chip tone={statusTone}>{feedback.status}</Chip>
            <span className="font-mono text-[10.5px] tracking-wider text-fc-text-dim">
              {formatRelativeTime(feedback.createdAt, now)}
            </span>
          </div>
        </div>
      </section>

      {/* MESSAGE */}
      <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <div className="text-[13px] font-semibold text-fc-text">Message</div>
            <div className="text-[11.5px] text-fc-text-dim">
              As submitted by user
            </div>
          </div>
          <Link
            to={`/admin/users/${feedback.userId}`}
            className="rounded-md border border-fc-border bg-fc-overlay px-3 py-1 text-[11.5px] text-fc-text hover:border-fc-brand"
          >
            View user profile →
          </Link>
        </div>
        <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-fc-text">
          {feedback.message}
        </div>
        <div className="mt-4 border-t border-fc-border pt-3 text-[11px] text-fc-text-dim">
          Submitted by{" "}
          <span className="font-medium text-fc-text-secondary">
            {feedback.userName}
          </span>{" "}
          <span className="mx-1">·</span>
          <span className="font-mono">{initials(feedback.userName)}</span>
        </div>
      </section>

      {/* CONTACT — only when the user left a way to reach them */}
      {feedback.contactEmail || feedback.contactPhone ? (
        <section className="rounded-xl border border-fc-brand/30 bg-fc-brand/5 p-4">
          <div className="text-[10.5px] uppercase tracking-wide text-fc-brand">
            Contact for follow-up
          </div>
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
            {feedback.contactEmail ? (
              <a
                href={`mailto:${feedback.contactEmail}`}
                className="text-fc-text hover:text-fc-brand"
              >
                ✉️ {feedback.contactEmail}
              </a>
            ) : null}
            {feedback.contactPhone ? (
              <a
                href={`tel:${formatPhone(feedback.contactPhone).tel}`}
                className="font-mono text-fc-text hover:text-fc-brand"
              >
                📞 {formatPhone(feedback.contactPhone).display}
              </a>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* SCREENSHOT */}
      {feedback.screenshotUrl ? (
        <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
          <div className="mb-3">
            <div className="text-[13px] font-semibold text-fc-text">
              Screenshot
            </div>
            <div className="text-[11.5px] text-fc-text-dim">
              Attached by user
            </div>
          </div>
          <a
            href={feedback.screenshotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block overflow-hidden rounded-lg border border-fc-border"
          >
            <img
              src={feedback.screenshotUrl}
              alt="Feedback screenshot"
              className="max-h-96 w-full object-contain bg-fc-bg"
            />
          </a>
        </section>
      ) : null}

      {/* DEVICE INFO */}
      {feedback.deviceInfo ? (
        <section className="rounded-xl border border-fc-info/20 bg-fc-info/5 p-4">
          <div className="text-[10.5px] uppercase tracking-wide text-fc-info">
            Device
          </div>
          <div className="mt-1 font-mono text-[12.5px] text-fc-text">
            {feedback.deviceInfo}
          </div>
        </section>
      ) : null}

      {/* PROP GRID */}
      <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
        <div className="mb-4">
          <div className="text-[13px] font-semibold text-fc-text">
            Feedback properties
          </div>
          <div className="text-[11.5px] text-fc-text-dim">
            Fields from schema
          </div>
        </div>
        <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5">
          <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
            Feedback ID
          </div>
          <div className="text-[12.5px] text-fc-text">
            <span className="rounded-md border border-fc-border bg-fc-overlay px-2 py-0.5 font-mono text-[11.5px]">
              {feedback.id}
            </span>
          </div>

          <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
            Category
          </div>
          <div className="text-[12.5px] text-fc-text">{feedback.category}</div>

          <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
            Status
          </div>
          <div className="text-[12.5px] text-fc-text">
            <Chip tone={statusTone}>{feedback.status}</Chip>
          </div>

          <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
            User ID
          </div>
          <div className="text-[12.5px] text-fc-text">
            <span className="font-mono text-[11.5px]">{feedback.userId}</span>
          </div>

          <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
            Created
          </div>
          <div className="text-[12.5px] text-fc-text">
            <span className="font-mono text-[12px]">
              {toDateTime(feedback.createdAt)}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
